import { Text, Box, Grid, Badge, Group, Paper } from "@mantine/core";

interface Props {
  data?: Record<string, any>;
  showAi?: any;
}

const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// Helper function to handle boolean values
const formatValue = (value: any) => {
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  } else if (typeof value === "string" && value.toLowerCase() === "true") {
    return "Yes";
  } else if (typeof value === "string" && value.toLowerCase() === "false") {
    return "No";
  }
  return capitalizeFirstLetter(String(value));
};

const Card: React.FC<Props> = ({ data = {}, showAi }) => {
  const renderData = (data: any, level: number = 0) => {

    if (Array.isArray(data)) {
      return data.map((item, index) => (
        <Grid.Col span={2} key={index}>
          {renderData(item, level + 1)}
        </Grid.Col>
      ));
    } else if (typeof data === "object") {
      return Object.keys(data).map((key, index) => (
        <Grid.Col span={4} key={index}>
          <Paper
            shadow="sm"
            sx={{
              display: "flex",
              flexDirection: "column",
              padding: "12px",
              gap: "18px",
              backgroundColor: "#FFFFFF",
              height: "100%",
            }}
          >
            <Box w={200}>
              <Text
                weight={500}
                color="#474E5B"
                size={"14px"}
                sx={{ width: "200px" }}
                truncate="end"
              >
                {`${capitalizeFirstLetter(key.replace(/_/g, " "))} `}
              </Text>
            </Box>
            <Box>{renderData(data[key], level + 1)}</Box>
          </Paper>
        </Grid.Col>
      ));
    } else if (typeof data === "string") {
      const parts = data.split(",").slice(0, 4);
      const getWidth = (count: any) => {
        if (count > 2) return 70;
        return "auto";
      };
      const getMaxWidth = (count: any) => {
        if (count > 2) return 50;
        return 200;
      };
      return parts.length > 0 &&
        parts.some((part) => part.trim().length > 0) ? (
        parts.map((part, index) => (
          <Badge radius="sm" color="gray" key={index}>
            <Box w={getWidth(parts.length)} maw={getMaxWidth(parts.length)}>
              <Text truncate="end">
                {formatValue(part.trim()) ?? "--"}
              </Text>
            </Box>
          </Badge>
        ))
      ) : (
        <>
          <Badge radius="sm" color="gray">
            --
          </Badge>
        </>
      );
    } else {
      return (
        <Badge radius="sm" color="gray">
          <Text size="md">{formatValue(data)}</Text>
        </Badge>
      );
    }
  };

  const renderDataForAi = (data: any) => {
    return (
      <Box
        sx={{
          padding: "16px",
          borderRadius: "8px",
          backgroundColor: "#FAFAFA",
        }}
      >
        <Grid>
          {Object.keys(data).map((sectionKey, sectionIndex) => {
            const sectionData = data[sectionKey];

            // Check if data is available for the current section
            if (
              !sectionData ||
              (typeof sectionData === "object" &&
                Object.keys(sectionData).length === 0) ||
              (Array.isArray(sectionData) && sectionData.length === 0)
            ) {
              return null; // Skip rendering this section if no data
            }

            return (
              <Grid.Col span={4} key={sectionIndex}>
                <Paper
                  shadow="sm"
                  sx={{
                    marginBottom: "12px",
                    backgroundColor: "#FFFFFF",
                    padding: "12px",
                    height: "100%",
                  }}
                >
                  <Text
                    weight={550}
                    color="#474E5B"
                    size={"14px"}
                    sx={{ width: "200px" }}
                    truncate="end"
                  >
                    {capitalizeFirstLetter(sectionKey.replace(/_/g, " "))}
                  </Text>
                  {typeof sectionData === "object" &&
                  !Array.isArray(sectionData) ? (
                    <Box sx={{ marginTop: "8px" }}>
                      <Grid>
                        {Object.keys(sectionData)
                          .slice(0, 4) // Limit to 4 items
                          .map((key, index) => (
                            <Grid.Col span={12} key={index}>
                              <Group
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <Text
                                  weight={500}
                                  color="#474E5B"
                                  size={"14px"}
                                  sx={{ width: "100px" }}
                                  truncate="end"
                                >
                                  {capitalizeFirstLetter(
                                    key.replace(/_/g, " ")
                                  )}
                                  :
                                </Text>
                                <Badge radius="sm" color="gray" w={100}>
                                  <Text truncate="end">
                                    {formatValue(sectionData[key])}
                                  </Text>
                                </Badge>
                              </Group>
                            </Grid.Col>
                          ))}
                      </Grid>
                    </Box>
                  ) : Array.isArray(sectionData) ? (
                    sectionData
                      .slice(0, 4) // Limit to 4 items
                      .map((item: any, itemIndex: number) => (
                        <Box key={itemIndex} sx={{ marginTop: "8px" }}>
                          <Grid>
                            {Object.keys(item)
                              .slice(0, 4) // Limit to 4 items
                              .map((key, index) => (
                                <Grid.Col span={12} key={index}>
                                  <Group
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                    }}
                                  >
                                    <Text
                                      weight={500}
                                      color="#474E5B"
                                      size={"14px"}
                                      sx={{ width: "100px" }}
                                      truncate="end"
                                    >
                                      {capitalizeFirstLetter(
                                        key.replace(/_/g, " ")
                                      )}
                                      :
                                    </Text>
                                    <Badge radius="sm" color="gray" w={100}>
                                      <Text truncate="end">
                                        {formatValue(item[key])}
                                      </Text>
                                    </Badge>
                                  </Group>
                                </Grid.Col>
                              ))}
                          </Grid>
                        </Box>
                      ))
                  ) : (
                    <Group
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <Text
                        weight={500}
                        color="#474E5B"
                        size={"14px"}
                        sx={{ width: "100px" }}
                        truncate="end"
                      >
                        {capitalizeFirstLetter(sectionKey.replace(/_/g, " "))}:
                      </Text>
                      <Badge radius="sm" color="gray" w={100}>
                        <Text truncate="end">
                          {formatValue(sectionData)}
                        </Text>
                      </Badge>
                    </Group>
                  )}
                </Paper>
              </Grid.Col>
            );
          })}
        </Grid>
      </Box>
    );
  };

  return (
    <>
      {showAi !== 1 ? (
        <Box
          sx={{
            padding: "16px",
            borderRadius: "8px",
            backgroundColor: "#FAFAFA",
          }}
        >
          <Grid>{renderData(data)}</Grid>
        </Box>
      ) : (
        renderDataForAi(data)
      )}
    </>
  );
};

export default Card;
